import { useState, type ReactNode } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Input,
  SettingRow,
  Slider,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
} from "@coval/ui"
import { Eye, EyeOff, Settings2 } from "lucide-react"

import { ReadOnlyFieldset } from "@/components/version-history/ReadOnlyRegion"
import {
  getTimeoutSecondsError,
  parseTimeoutSecondsInput,
} from "@/utils/simulation/realtimeFieldValidation"
import {
  isOpenAIRealtimeApiKeySource,
  OPENAI_GPT_LIVE_ALPHA_MODELS,
  OPENAI_REALTIME_API_KEY_SOURCE,
  type OpenAIRealtimeApiKeySource,
} from "@/utils/simulation/simulatorFields"

interface AdvancedSettingsCardProps {
  children: ReactNode
}

export function RealtimeAdvancedSettingsCard({
  children,
}: AdvancedSettingsCardProps) {
  return (
    <Accordion
      type="single"
      collapsible
      className="rounded-md border border-coval-border bg-surface-default"
    >
      <AccordionItem value="advanced" className="border-b-0">
        <AccordionTrigger className="px-6 py-5 text-sm font-semibold">
          <span className="flex items-center gap-2">
            <Settings2 className="size-4 shrink-0" />
            Advanced Settings
          </span>
        </AccordionTrigger>
        <AccordionContent className="px-6">
          <ReadOnlyFieldset>{children}</ReadOnlyFieldset>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

interface TimeoutSettingsProps {
  timeoutSeconds?: number
  onTimeoutSecondsChange: (value: number | undefined) => void
  disabled?: boolean
  className?: string
}

function TimeoutSetting({
  timeoutSeconds,
  onTimeoutSecondsChange,
  disabled,
  className,
}: TimeoutSettingsProps) {
  const timeoutError = getTimeoutSecondsError(timeoutSeconds)

  return (
    <SettingRow
      label="Timeout"
      tooltip="Maximum simulation duration in seconds. Defaults to 900."
      error={timeoutError}
    >
      {(controlId) => (
        <Input
          id={controlId}
          type="number"
          min={1}
          max={1800}
          value={timeoutSeconds?.toString() || ""}
          placeholder="Set a timeout in seconds"
          onChange={(event) =>
            onTimeoutSecondsChange(parseTimeoutSecondsInput(event.target.value))
          }
          className={`w-28 ${className || ""}`}
          disabled={disabled}
          error={!!timeoutError}
        />
      )}
    </SettingRow>
  )
}

interface GeminiRealtimeAdvancedFieldsProps extends TimeoutSettingsProps {
  temperature?: number
  onTemperatureChange: (value: number | undefined) => void
}

export function GeminiRealtimeAdvancedFields({
  temperature,
  onTemperatureChange,
  ...timeoutProps
}: GeminiRealtimeAdvancedFieldsProps) {
  return (
    <div className="space-y-1">
      <SettingRow
        label="Temperature"
        tooltip="Controls how varied the model's responses are. Defaults to 0.8."
        stacked
        action={
          <span className="text-sm font-medium text-coval-text">
            {temperature !== undefined ? temperature.toFixed(1) : "0.8"}
          </span>
        }
      >
        <Slider
          aria-label="Temperature"
          step={0.1}
          min={0}
          max={2}
          value={[temperature !== undefined ? temperature : 0.8]}
          onValueChange={(values) => onTemperatureChange(values[0])}
          className={timeoutProps.className}
          disabled={timeoutProps.disabled}
        />
      </SettingRow>
      <TimeoutSetting {...timeoutProps} />
    </div>
  )
}

export function GrokRealtimeAdvancedFields(props: TimeoutSettingsProps) {
  return <TimeoutSetting {...props} />
}

interface OpenAIRealtimeAdvancedFieldsProps {
  showTuning?: boolean
  apiKey: string
  apiKeySource: OpenAIRealtimeApiKeySource
  model?: string
  webSearchEnabled?: boolean
  temperature?: number
  timeoutSeconds?: number
  onApiKeyChange: (value: string) => void
  onApiKeySourceChange: (value: OpenAIRealtimeApiKeySource) => void
  onWebSearchEnabledChange?: (value: boolean) => void
  onTemperatureChange: (value: number | undefined) => void
  onTimeoutSecondsChange: (value: number | undefined) => void
  disabled?: boolean
  className?: string
}

export function OpenAIRealtimeAdvancedFields({
  showTuning = true,
  apiKey,
  apiKeySource,
  model,
  webSearchEnabled,
  temperature,
  timeoutSeconds,
  onApiKeyChange,
  onApiKeySourceChange,
  onWebSearchEnabledChange,
  onTemperatureChange,
  onTimeoutSecondsChange,
  disabled,
  className,
}: OpenAIRealtimeAdvancedFieldsProps) {
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false)
  const isGptLiveAlphaSelected = OPENAI_GPT_LIVE_ALPHA_MODELS.some(
    (modelOption) => modelOption.value === model,
  )

  return (
    <div className="space-y-1">
      {showTuning && (
        <GeminiRealtimeAdvancedFields
          temperature={temperature}
          timeoutSeconds={timeoutSeconds}
          onTemperatureChange={onTemperatureChange}
          onTimeoutSecondsChange={onTimeoutSecondsChange}
          disabled={disabled}
          className={className}
        />
      )}
      <SettingRow
        label="API Key Source"
        tooltip="Choose Coval's managed credential or provide your own OpenAI API key."
        stacked
      >
        <Tabs
          value={apiKeySource}
          onValueChange={(value) => {
            if (isOpenAIRealtimeApiKeySource(value)) {
              setIsApiKeyVisible(false)
              onApiKeySourceChange(value)
            }
          }}
        >
          <TabsList variant="pill" aria-label="API Key Source">
            <TabsTrigger
              value={OPENAI_REALTIME_API_KEY_SOURCE.COVAL_MANAGED}
              variant="pill"
              disabled={disabled}
            >
              Coval Managed
            </TabsTrigger>
            <TabsTrigger
              value={OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED}
              variant="pill"
              disabled={disabled}
            >
              Custom
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </SettingRow>
      {apiKeySource === OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED && (
        <SettingRow label="OpenAI API Key" stacked>
          {(controlId) => (
            <Input
              id={controlId}
              type={isApiKeyVisible ? "text" : "password"}
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore
              data-op-ignore
              data-bwignore="true"
              value={apiKey}
              placeholder="Enter your OpenAI API key"
              onChange={(event) => onApiKeyChange(event.target.value)}
              className={className}
              disabled={disabled}
              required
              endAdornment={
                <Tooltip
                  content={isApiKeyVisible ? "Hide API key" : "Show API key"}
                >
                  <button
                    type="button"
                    onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                    aria-label={
                      isApiKeyVisible ? "Hide API key" : "Show API key"
                    }
                  >
                    {isApiKeyVisible ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </Tooltip>
              }
            />
          )}
        </SettingRow>
      )}
      {onWebSearchEnabledChange && !isGptLiveAlphaSelected && (
        <SettingRow
          label="Web Search"
          tooltip="Allow the agent to search the web for up-to-date information during the conversation."
        >
          {(controlId) => (
            <Switch
              id={controlId}
              checked={webSearchEnabled ?? false}
              onCheckedChange={onWebSearchEnabledChange}
              disabled={disabled}
            />
          )}
        </SettingRow>
      )}
    </div>
  )
}
